/**
 * AdminLibraryTab Component
 * 
 * Manages the Library section for book recommendations and downloadable resources.
 * Allows admins to add, edit, and delete books and resources.
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BookOpen, Plus, Edit, Trash2, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BOOK_CATEGORIES = ['Growth', 'Leadership', 'Mindset', 'Faith', 'Health', 'Business', 'Relationships', 'Self-Care'];
const RESOURCE_CATEGORIES = ['Worksheets', 'Templates', 'Guides', 'Checklists', 'Journals', 'General'];
const ICON_OPTIONS = ['BookOpen', 'Heart', 'Star', 'Sparkles', 'Flame', 'Crown', 'Diamond', 'Download', 'FileText'];

const AdminLibraryTab = () => {
  // Books state
  const [libraryBooks, setLibraryBooks] = useState([]);
  const [showLibraryBookDialog, setShowLibraryBookDialog] = useState(false);
  const [editingLibraryBook, setEditingLibraryBook] = useState(null);
  const [libraryBookForm, setLibraryBookForm] = useState({
    title: '',
    author: '',
    description: '',
    category: 'Growth',
    amazon_link: '',
    recommended_by: 'Crystal & Dr. Nicole',
    icon: 'BookOpen'
  });

  // Resources state
  const [libraryResources, setLibraryResources] = useState([]);
  const [showLibraryResourceDialog, setShowLibraryResourceDialog] = useState(false);
  const [editingLibraryResource, setEditingLibraryResource] = useState(null);
  const [libraryResourceForm, setLibraryResourceForm] = useState({
    title: '',
    description: '',
    file_url: '',
    category: 'General',
    icon: 'Download'
  });

  const [uploadingResource, setUploadingResource] = useState(false);

  useEffect(() => {
    fetchLibraryData();
  }, []);

  const fetchLibraryData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [booksRes, resourcesRes] = await Promise.all([
        axios.get(`${API_URL}/api/library/books`, { headers }),
        axios.get(`${API_URL}/api/library/resources`, { headers })
      ]);
      setLibraryBooks(booksRes.data.books || []);
      setLibraryResources(resourcesRes.data.resources || []);
    } catch (error) {
      console.error('Error fetching library data:', error);
    }
  };

  // Book handlers
  const handleSaveLibraryBook = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      if (editingLibraryBook) {
        await axios.put(`${API_URL}/api/admin/library/books/${editingLibraryBook.id}`, libraryBookForm, { headers });
        toast.success('Book updated successfully');
      } else {
        await axios.post(`${API_URL}/api/admin/library/books`, libraryBookForm, { headers });
        toast.success('Book added successfully');
      }
      setShowLibraryBookDialog(false);
      fetchLibraryData();
    } catch (error) {
      console.error('Error saving book:', error);
      toast.error('Failed to save book');
    }
  };

  const handleDeleteLibraryBook = async (bookId) => {
    if (!window.confirm('Are you sure you want to delete this book?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/admin/library/books/${bookId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Book deleted');
      fetchLibraryData();
    } catch (error) {
      console.error('Error deleting book:', error);
      toast.error('Failed to delete book');
    }
  };

  // Resource handlers
  const handleSaveLibraryResource = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      if (editingLibraryResource) {
        await axios.put(`${API_URL}/api/admin/library/resources/${editingLibraryResource.id}`, libraryResourceForm, { headers });
        toast.success('Resource updated successfully');
      } else {
        await axios.post(`${API_URL}/api/admin/library/resources`, libraryResourceForm, { headers });
        toast.success('Resource added successfully');
      }
      setShowLibraryResourceDialog(false);
      fetchLibraryData();
    } catch (error) {
      console.error('Error saving resource:', error);
      toast.error('Failed to save resource');
    }
  };

  const handleDeleteLibraryResource = async (resourceId) => {
    if (!window.confirm('Are you sure you want to delete this resource?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/admin/library/resources/${resourceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Resource deleted');
      fetchLibraryData();
    } catch (error) {
      console.error('Error deleting resource:', error);
      toast.error('Failed to delete resource');
    }
  };

  const handleResourceFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingResource(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/admin/library/upload-resource`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      setLibraryResourceForm(prev => ({ ...prev, file_url: response.data.file_url }));
      toast.success('File uploaded successfully');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploadingResource(false);
    }
  };

  return (
    <>
      <Card className="card-base">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-terracotta" />
              Library Management
            </CardTitle>
          </div>
          <CardDescription>Manage book recommendations and downloadable resources for members</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="books" className="space-y-4">
            <TabsList>
              <TabsTrigger value="books">Book Recommendations</TabsTrigger>
              <TabsTrigger value="resources">Downloadable Resources</TabsTrigger>
            </TabsList>
            
            {/* Books Management */}
            <TabsContent value="books" className="space-y-4">
              <Button 
                onClick={() => {
                  setEditingLibraryBook(null);
                  setLibraryBookForm({ 
                    title: '', 
                    author: '', 
                    description: '', 
                    category: 'Growth', 
                    amazon_link: '', 
                    recommended_by: 'Crystal & Dr. Nicole', 
                    icon: 'BookOpen' 
                  });
                  setShowLibraryBookDialog(true);
                }}
                className="btn-primary"
              >
                <Plus className="h-4 w-4 mr-2" /> Add Book
              </Button>
              
              <div className="space-y-3">
                {libraryBooks.map(book => (
                  <div key={book.id} className="flex items-center justify-between p-4 bg-sand/30 rounded-xl">
                    <div className="flex-1">
                      <h4 className="font-medium text-forest">{book.title}</h4>
                      <p className="text-sm text-gray-500">by {book.author} | {book.category}</p>
                      <p className="text-xs text-gray-400">Recommended by {book.recommended_by}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => {
                        setEditingLibraryBook(book);
                        setLibraryBookForm(book);
                        setShowLibraryBookDialog(true);
                      }}>
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDeleteLibraryBook(book.id)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
                {libraryBooks.length === 0 && (
                  <p className="text-center text-gray-500 py-4">No books added yet. Default recommendations will be shown to members.</p>
                )}
              </div>
            </TabsContent>
            
            {/* Resources Management */}
            <TabsContent value="resources" className="space-y-4">
              <Button 
                onClick={() => {
                  setEditingLibraryResource(null);
                  setLibraryResourceForm({ 
                    title: '', 
                    description: '', 
                    file_url: '', 
                    category: 'General', 
                    icon: 'Download' 
                  });
                  setShowLibraryResourceDialog(true);
                }}
                className="btn-primary"
              >
                <Plus className="h-4 w-4 mr-2" /> Add Resource
              </Button>
              
              <div className="space-y-3">
                {libraryResources.map(resource => (
                  <div key={resource.id} className="flex items-center justify-between p-4 bg-sand/30 rounded-xl">
                    <div className="flex-1">
                      <h4 className="font-medium text-forest">{resource.title}</h4>
                      <p className="text-sm text-gray-500">{resource.category}</p>
                      <a href={resource.file_url} target="_blank" rel="noopener noreferrer" className="text-xs text-terracotta hover:underline">
                        View File
                      </a>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => {
                        setEditingLibraryResource(resource);
                        setLibraryResourceForm(resource);
                        setShowLibraryResourceDialog(true);
                      }}>
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDeleteLibraryResource(resource.id)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
                {libraryResources.length === 0 && (
                  <p className="text-center text-gray-500 py-4">No resources added yet. Default resources will be shown to members.</p>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Book Dialog */}
      <Dialog open={showLibraryBookDialog} onOpenChange={setShowLibraryBookDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingLibraryBook ? 'Edit Book' : 'Add Book Recommendation'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input
                value={libraryBookForm.title}
                onChange={(e) => setLibraryBookForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Book title"
              />
            </div>
            <div>
              <Label>Author</Label>
              <Input
                value={libraryBookForm.author}
                onChange={(e) => setLibraryBookForm(prev => ({ ...prev, author: e.target.value }))}
                placeholder="Author name"
              />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea
                value={libraryBookForm.description || ''}
                onChange={(e) => setLibraryBookForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Why do you recommend this book?"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select
                  value={libraryBookForm.category}
                  onValueChange={(v) => setLibraryBookForm(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BOOK_CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Icon</Label>
                <Select
                  value={libraryBookForm.icon}
                  onValueChange={(v) => setLibraryBookForm(prev => ({ ...prev, icon: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ICON_OPTIONS.map(icon => (
                      <SelectItem key={icon} value={icon}>{icon}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Amazon Link</Label>
              <Input
                value={libraryBookForm.amazon_link || ''}
                onChange={(e) => setLibraryBookForm(prev => ({ ...prev, amazon_link: e.target.value }))}
                placeholder="https://amazon.com/..."
              />
            </div>
            <div>
              <Label>Recommended By</Label>
              <Input
                value={libraryBookForm.recommended_by}
                onChange={(e) => setLibraryBookForm(prev => ({ ...prev, recommended_by: e.target.value }))}
                placeholder="Crystal & Dr. Nicole"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowLibraryBookDialog(false)}>Cancel</Button>
              <Button className="btn-primary" onClick={handleSaveLibraryBook}>
                {editingLibraryBook ? 'Update' : 'Add'} Book
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Resource Dialog */}
      <Dialog open={showLibraryResourceDialog} onOpenChange={setShowLibraryResourceDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingLibraryResource ? 'Edit Resource' : 'Add Downloadable Resource'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input
                value={libraryResourceForm.title}
                onChange={(e) => setLibraryResourceForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Resource title"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={libraryResourceForm.description || ''}
                onChange={(e) => setLibraryResourceForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe this resource"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select
                  value={libraryResourceForm.category}
                  onValueChange={(v) => setLibraryResourceForm(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RESOURCE_CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Icon</Label>
                <Select
                  value={libraryResourceForm.icon}
                  onValueChange={(v) => setLibraryResourceForm(prev => ({ ...prev, icon: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ICON_OPTIONS.map(icon => (
                      <SelectItem key={icon} value={icon}>{icon}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>File</Label>
              <div className="flex gap-2">
                <Input
                  value={libraryResourceForm.file_url || ''}
                  onChange={(e) => setLibraryResourceForm(prev => ({ ...prev, file_url: e.target.value }))}
                  placeholder="File URL or upload below"
                  className="flex-1"
                />
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.xlsx,.pptx"
                    onChange={handleResourceFileUpload}
                    className="hidden"
                  />
                  <Button variant="outline" type="button" disabled={uploadingResource} asChild>
                    <span>
                      {uploadingResource ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    </span>
                  </Button>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowLibraryResourceDialog(false)}>Cancel</Button>
              <Button className="btn-primary" onClick={handleSaveLibraryResource}>
                {editingLibraryResource ? 'Update' : 'Add'} Resource
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminLibraryTab;
